import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { forwardRef, useId, type ComponentPropsWithoutRef, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

/*
 * Primitivas de interface.
 *
 * Padrao shadcn/ui, escritas no projeto em vez de instaladas: sao poucas, e
 * manter o codigo aqui evita que um upgrade do gerador reescreva componentes
 * que a paroquia ja esta usando.
 */

// --- Botao ----------------------------------------------------------------

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium transition-colors ' +
    'disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'bg-primario text-sobre-primario hover:bg-primario-forte active:bg-primario-pressionado',
        secondary: 'bg-primario-suave text-primario hover:bg-borda',
        outline: 'border border-borda bg-transparent hover:bg-afundado',
        ghost: 'hover:bg-primario-suave hover:text-primario',
        danger: 'bg-perigo text-sobre-primario hover:bg-perigo-forte',
        link: 'text-primario underline underline-offset-4 hover:text-primario-forte',
      },
      size: {
        sm: 'h-9 px-3 text-sm',
        md: 'h-11 px-4',
        lg: 'h-12 px-6 text-base',
        icon: 'size-11',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
)

export interface ButtonProps
  extends ComponentPropsWithoutRef<'button'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Component = asChild ? Slot : 'button'
    return (
      <Component
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

// --- Campo de formulario --------------------------------------------------

export const Input = forwardRef<HTMLInputElement, ComponentPropsWithoutRef<'input'>>(
  ({ className, ...props }, ref) => (
    <input
      ref={ref}
      className={cn(
        'h-11 w-full rounded-md border border-borda bg-cartao px-3',
        'text-conteudo placeholder:text-suave',
        'disabled:cursor-not-allowed disabled:opacity-60',
        'aria-[invalid=true]:border-perigo aria-[invalid=true]:ring-1 aria-[invalid=true]:ring-perigo',
        className,
      )}
      {...props}
    />
  ),
)
Input.displayName = 'Input'

export const Textarea = forwardRef<HTMLTextAreaElement, ComponentPropsWithoutRef<'textarea'>>(
  ({ className, ...props }, ref) => (
    <textarea
      ref={ref}
      className={cn(
        'min-h-24 w-full rounded-md border border-borda bg-cartao px-3 py-2',
        'text-conteudo placeholder:text-suave',
        'aria-[invalid=true]:border-perigo',
        className,
      )}
      {...props}
    />
  ),
)
Textarea.displayName = 'Textarea'

interface FieldProps {
  label: string
  /** Mensagens vindas de ApiError.fields ou da validacao local. */
  error?: string | string[]
  hint?: string
  required?: boolean
  children: (props: { id: string; 'aria-invalid': boolean; 'aria-describedby': string }) => ReactNode
}

/**
 * Rotulo + controle + erro, com a fiacao de acessibilidade pronta.
 *
 * O `children` recebe os ids em vez de o componente clonar o filho: assim o
 * erro fica ligado ao input por aria-describedby e o leitor de tela anuncia a
 * mensagem, em vez de o usuario cego descobrir o problema so ao enviar.
 */
export function Field({ label, error, hint, required, children }: FieldProps) {
  const id = useId()
  const messages = error === undefined ? [] : Array.isArray(error) ? error : [error]
  const hasError = messages.length > 0
  const errorId = `${id}-erro`
  const hintId = `${id}-dica`

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium">
        {label}
        {required && (
          <span className="ml-1 text-perigo" aria-label="obrigatorio">
            *
          </span>
        )}
      </label>

      {children({
        id,
        'aria-invalid': hasError,
        'aria-describedby': [hint ? hintId : null, hasError ? errorId : null]
          .filter(Boolean)
          .join(' '),
      })}

      {hint && (
        <p id={hintId} className="text-sm text-suave">
          {hint}
        </p>
      )}
      {hasError && (
        <p id={errorId} role="alert" className="text-sm font-medium text-perigo">
          {messages.join('. ')}
        </p>
      )}
    </div>
  )
}

// --- Cartao ---------------------------------------------------------------

export function Card({ className, ...props }: ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      className={cn(
        'rounded-lg border border-borda bg-cartao shadow-sm',
        className,
      )}
      {...props}
    />
  )
}

export function CardHeader({ className, ...props }: ComponentPropsWithoutRef<'div'>) {
  return <div className={cn('border-b border-borda p-5', className)} {...props} />
}

export function CardBody({ className, ...props }: ComponentPropsWithoutRef<'div'>) {
  return <div className={cn('p-5', className)} {...props} />
}

// --- Aviso ----------------------------------------------------------------

const alertVariants = cva('rounded-md border p-4 text-sm', {
  variants: {
    tone: {
      info: 'border-info bg-info-suave text-info',
      success: 'border-sucesso bg-sucesso-suave text-sucesso',
      warning: 'border-aviso bg-aviso-suave text-aviso',
      danger: 'border-perigo bg-perigo-suave text-perigo',
    },
  },
  defaultVariants: { tone: 'info' },
})

export interface AlertProps
  extends ComponentPropsWithoutRef<'div'>,
    VariantProps<typeof alertVariants> {}

export function Alert({ className, tone, ...props }: AlertProps) {
  return (
    // role=alert faz o leitor de tela anunciar sem o usuario precisar procurar.
    <div role="alert" className={cn(alertVariants({ tone }), className)} {...props} />
  )
}

// --- Carregando -----------------------------------------------------------

export function Spinner({ label = 'Carregando' }: { label?: string }) {
  return (
    <span role="status" aria-live="polite" className="inline-flex items-center gap-2">
      <span
        aria-hidden="true"
        className="size-4 animate-spin rounded-full border-2 border-borda-forte border-t-primario"
      />
      <span className="sr-only">{label}</span>
    </span>
  )
}

export function PageLoader() {
  return (
    <div className="flex min-h-64 items-center justify-center">
      <Spinner label="Carregando pagina" />
    </div>
  )
}
